import React, { useEffect, useState } from 'react'
import { ContentServiceFlowStatus, FlowResult, RequestError } from './types'
import { useStatusPolling } from './hooks'
import { getWorkerFlowResult, isRequestError } from './utility'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from '@mui/material'
import { StatusState } from './StatusState'
import { StatusDetails } from './StatusDetails'
import { OutlinedButton } from 'shared/ui'
import { openTab } from '../Helpers'
import { FlowError, FlowResults } from './JobView'

export function FlowStatusGroup({
  flowIds,
  completedItems,
  onFlowDelete,
  onFlowPoll,
}: {
  flowIds: string[]
  completedItems?: React.ReactNode[]
  onFlowDelete?: (flowId: string) => void
  onFlowPoll?: (
    flowId: string,
    flowStatus: ContentServiceFlowStatus | RequestError | undefined,
  ) => void
}) {
  if (!flowIds.length) return null
  const getOnDelete = (flowId: string) => {
    if (!onFlowDelete) return
    return () => onFlowDelete(flowId)
  }
  return (
    <Box paddingTop="15px">
      <Box>Job Status</Box>
      {flowIds.map((flowId) => (
        <JobStatus
          key={flowId}
          flowId={flowId}
          completedItems={completedItems}
          onFlowDelete={getOnDelete(flowId)}
          onFlowPoll={onFlowPoll}
        />
      ))}
    </Box>
  )
}

function JobStatus({
  flowId,
  completedItems,
  onFlowDelete,
  onFlowPoll,
}: {
  flowId: string
  completedItems?: React.ReactNode[]
  onFlowDelete?: () => void
  onFlowPoll?: (
    flowId: string,
    flowStatus: ContentServiceFlowStatus | RequestError | undefined,
  ) => void
}) {
  const [status, setStatus] = useState<
    ContentServiceFlowStatus | RequestError
  >()

  useStatusPolling({
    flowId,
    onFlowPoll: (flowStatus) => {
      setStatus(flowStatus)
      onFlowPoll?.(flowId, flowStatus)
    },
  })

  if (status && isRequestError(status)) {
    return <Box padding="8px">{`Error: ${status.errorText}`}</Box>
  }

  return (
    <Accordion>
      <AccordionSummary>
        {status ? (
          <StatusState flowStatus={status} onFlowDelete={onFlowDelete} />
        ) : (
          flowId
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Box paddingBottom="8px">
          {status && <StatusDetails flowStatus={status} />}
        </Box>
        <Box>
          <JobStatusResults status={status} completedItems={completedItems} />
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

function JobStatusResults({
  status,
  completedItems,
}: {
  status?: ContentServiceFlowStatus
  completedItems?: React.ReactNode[]
}) {
  const [flowResult, setFlowResult] = useState<FlowResult>()
  const [getResult, setGetResult] = useState(false)
  const [error, setError] = useState<unknown>()

  const flowId = status?.data.details.flow_id
  const completed = status?.data.completed

  useEffect(() => {
    if (!getResult || !completed || !flowId) return
    getWorkerFlowResult(flowId).then(({ result, error }) => {
      setFlowResult(result)
      setError(error)
    })
  }, [getResult, status, completed, flowId])

  if (!completed) return null

  return (
    <Box>
      {completedItems}
      {!getResult && (
        <OutlinedButton onClick={() => setGetResult(true)}>
          Get Result
        </OutlinedButton>
      )}
      <OutlinedButton
        onClick={() => openTab('/content_service/flow/' + flowId)}
      >
        Open Tab
      </OutlinedButton>
      <FlowError error={error} />
      <FlowResults flowResult={flowResult} />
    </Box>
  )
}
